<template>
  <v-app>
    <PreloaderComponent :enable="enable || loading"></PreloaderComponent>

    <v-container v-if="result">
      <BreadcrumbComponent :items="items"></BreadcrumbComponent>

      <!-- Registration dialog (only used for first-time registration) -->
      <v-dialog v-model="dialog" max-width="600px">
        <v-card>
          <v-card-title class="headline white--text outlined text-center">
            <div class="d-flex justify-center" style="width: 100%;">
              <img src="../../assets/natcash.png" style="margin: auto" height="50px"/>
            </div>
          </v-card-title>

          <v-divider></v-divider>
          <v-card-text class="pt-4">
            <v-form ref="form">
              <v-container>
                <v-row cols="12" sm="6" class="mt-2">
                  <v-col cols="12" sm="6">
                    <v-text-field label="Nom" :disabled="loading || registration.status == 'ACCEPTED'"
                                  v-model="registration.last_name" required outlined class="rounded"></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field label="Prenom" :disabled="loading || registration.status == 'ACCEPTED'"
                                  v-model="registration.first_name" required outlined></v-text-field>
                  </v-col>
                </v-row>
                <v-row cols="12" sm="6" class="mt-3">
                  <v-col cols="12" sm="6">
                    <v-text-field label="Numero Natcash *" :rules="phoneRules" prefix="+509" type="tel"
                                  maxlength="8" :disabled="loading || registration.status == 'ACCEPTED'"
                                  href="phone" v-model="registration.phone" required outlined></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field label="Numero Natcash * (Validation)" :rules="phoneMatchRules" prefix="+509"
                                  type="tel" :disabled="loading || registration.status == 'ACCEPTED'"
                                  maxlength="8" href="phone_verification" v-model="registration.phone_verification"
                                  required outlined></v-text-field>
                  </v-col>
                </v-row>

                <v-row cols="12" sm="6" class="mt-3">
                  <v-col cols="12" sm="6">
                    <v-select v-model="registration.document_type"
                              :disabled="loading || registration.status == 'ACCEPTED'" :items="doc_types"
                              item-text="name" item-value="id" label="Piece d'identite" required outlined></v-select>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field v-model="registration.document_type_number"
                                  :disabled="loading || registration.status == 'ACCEPTED'"
                                  label="Numero document d'identite" required outlined></v-text-field>
                  </v-col>
                </v-row>

                <v-row cols="12" sm="6" class="mt-3">
                  <v-col cols="12" sm="6">
                    <v-file-input
                        label="Televerser une photo de la piece d'identite"
                        accept="image/*"
                        dense
                        outlined
                        :disabled="loading || registration.status == 'ACCEPTED'"
                        v-model="registration.file"
                        class="rounded d-flex align-center justify-center"
                        prepend-icon="mdi-upload"
                    ></v-file-input>
                  </v-col>

                  <v-col cols="12" sm="6" v-if="registration.file || previewUrl"
                         class="d-flex align-center justify-center">
                    <v-img
                        :src="previewUrl"
                        max-width="250"
                        max-height="250"
                        contain
                        class="elevation-1 rounded"
                    ></v-img>
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-card-text>
          <v-divider v-show="registration.status != 'PENDING'"></v-divider>

          <v-row v-show="registration.id != null" justify="center">
            <v-chip
                :color="getProccesorColor(registration.status)"
                dark
                small
                class="ml-2"
            >
              {{ registration.status }}
            </v-chip>
          </v-row>
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-btn
                text
                color="red lighten-2"
                @click="dialog = false"
                class="mr-2"
            >
              Fermer
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
                v-show="registration.status != 'ACCEPTED'"
                color="primary"
                @click="register"
                :disabled="loading"
                class="ml-2"
            >
              <v-icon left>mdi-content-save</v-icon>
              Valider
            </v-btn>
          </v-card-actions>
        </v-card>
        <Spinner type="breathing" v-show="loading" style="margin-left: 70%" width="100px"
                 :src="require(`@/assets/img/emblem.png`)"/>
      </v-dialog>

      <!-- Account details view/edit dialog (for registered users) -->
      <v-dialog v-model="accountDialog" max-width="600px">
        <v-card>
          <v-card-title class="d-flex align-center">
            <img src="../../assets/natcash.png" height="30px" class="mr-3"/>
            Mon Compte Natcash
            <v-spacer></v-spacer>
            <v-chip
                v-if="registration.status"
                :color="getProccesorColor(registration.status)"
                dark
                small
            >
              {{ registration.status }}
            </v-chip>
          </v-card-title>

          <v-divider></v-divider>
          <v-card-text class="pt-5 pb-2">
            <!-- View-only account info -->
            <div class="info-row mb-4">
              <v-icon color="grey darken-1" class="mr-2" small>mdi-account</v-icon>
              <span class="info-label">Nom Complet</span>
              <span class="info-value">
                {{ account ? String(account.customer_first_name).toUpperCase() : '' }}
                {{ account ? String(account.customer_last_name).toUpperCase() : '' }}
              </span>
            </div>

            <div class="info-row mb-4">
              <v-icon color="grey darken-1" class="mr-2" small>mdi-phone</v-icon>
              <span class="info-label">Numero Natcash</span>
              <span class="info-value">{{ account ? account.customer_phone_number : '' }}</span>
            </div>

            <div class="info-row mb-4">
              <v-icon color="grey darken-1" class="mr-2" small>mdi-card-account-details</v-icon>
              <span class="info-label">Piece d'identite</span>
              <span class="info-value">{{ account ? account.customer_id_type : '' }} - {{ account ? account.customer_id_number : '' }}</span>
            </div>

            <div class="info-row mb-4" v-if="previewUrl">
              <v-icon color="grey darken-1" class="mr-2" small>mdi-file-image</v-icon>
              <span class="info-label">Document</span>
              <div class="mt-2" style="width: 100%;">
                <v-img
                    :src="previewUrl"
                    max-width="200"
                    max-height="150"
                    contain
                    class="elevation-1 rounded ml-6"
                ></v-img>
              </div>
            </div>

            <v-divider class="my-4"></v-divider>

            <!-- Editable minimum withdrawal amount -->
            <div class="d-flex align-center">
              <v-icon color="orange darken-2" class="mr-2" small>mdi-cash-edit</v-icon>
              <span class="info-label mr-3" style="white-space: nowrap;">Montant minimum de retrait</span>
            </div>
            <div class="mt-3 ml-6">
              <v-text-field
                  v-model="registration.minimum_withdrawal_amount"
                  suffix="HTG"
                  type="number"
                  min="250"
                  outlined
                  dense
                  :rules="[v => v >= 250 || 'Le montant minimum est de 250 HTG']"
                  style="max-width: 250px;"
              ></v-text-field>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-btn text color="red lighten-2" @click="accountDialog = false">
              Fermer
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="registerMinimumAMount" :disabled="loading">
              <v-icon left>mdi-content-save</v-icon>
              Enregistrer
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- COMPTE SECTION (above balance cards) -->
      <!-- If NOT registered: show register button -->
      <div v-if="registration.id == null && !loading" class="text-center my-6">
        <v-btn class="btn btn-info outlined text-lg-h6 action-btn" @click="dialog = true" min-width="200">
          Enregistrer mon compte <img src="../../assets/natcash.png" style="margin-top: -5px;" height="25px" class="ml-2"/>
        </v-btn>
      </div>

      <!-- If registered: show account card with edit button -->
      <v-card outlined class="mb-5 account-card" v-if="account">
        <v-card-title class="d-flex align-center">
          Compte
          <img src="../../assets/natcash.png" height="25px" class="ml-2"/>
          <v-spacer></v-spacer>
          <v-btn small outlined color="primary" @click="openEditDialog">
            <v-icon small left>mdi-pencil</v-icon>
            Modifier
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="py-4">
          <v-row align="center">
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-icon color="grey darken-1" class="mr-2">mdi-account</v-icon>
              <div>
                <div class="caption grey--text">Nom Complet</div>
                <div class="font-weight-medium">
                  {{ String(account.customer_first_name).toUpperCase() }}
                  {{ String(account.customer_last_name).toUpperCase() }}
                </div>
              </div>
            </v-col>

            <v-col cols="12" md="4" class="d-flex align-center">
              <v-icon color="grey darken-1" class="mr-2">mdi-phone</v-icon>
              <div>
                <div class="caption grey--text">Numero NATCASH</div>
                <div class="font-weight-medium">{{ account.customer_phone_number }}</div>
              </div>
            </v-col>

            <v-col cols="12" md="4" class="d-flex align-center">
              <v-icon color="grey darken-1" class="mr-2">mdi-cash-minus</v-icon>
              <div>
                <div class="caption grey--text">Montant minimum de retrait</div>
                <div class="font-weight-medium">{{ formatCurrency(account.minimum_withdrawal_amount) }} HTG</div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Balance cards -->
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="balance-card balance-card--total">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-2">
                <v-icon color="blue darken-2" class="mr-2">mdi-wallet</v-icon>
                <span class="balance-label">Balance Totale</span>
              </div>
              <div class="balance-amount">{{ formatCurrency(result.total_balance) }}</div>
              <div class="balance-currency">HTG</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="balance-card balance-card--due">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-2">
                <v-icon color="orange darken-2" class="mr-2">mdi-cash-clock</v-icon>
                <span class="balance-label">Montant du</span>
              </div>
              <div class="balance-amount">{{ formatCurrency(result.available_balance) }}</div>
              <div class="balance-currency">HTG</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="balance-card balance-card--available">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-2">
                <v-icon color="green darken-2" class="mr-2">mdi-cash-check</v-icon>
                <span class="balance-label">Disponible pour retrait</span>
              </div>
              <div class="balance-amount">{{ formatCurrency(result.available_for_withdrawal) }}</div>
              <div class="balance-currency">HTG</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card outlined class="balance-card balance-card--fees">
            <v-card-text class="pa-4">
              <div class="d-flex align-center mb-2">
                <v-icon color="red darken-1" class="mr-2">mdi-percent</v-icon>
                <span class="balance-label">Frais de transfert</span>
              </div>
              <div class="balance-amount">{{ formatCurrency(result.kl_fee) }}</div>
              <div class="balance-currency">HTG</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <div v-show="loading" class="ma-2">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <div v-show="message" class="mt-4">
        <v-alert dense dismissible :type="status">
          {{ message }}
        </v-alert>
      </div>

      <div class="mt-2">
        <v-alert type="info" outlined dense>
          Tous les montants sont en Gourdes Haitiennes (HTG)
        </v-alert>
      </div>

      <!-- Transactions table -->
      <div class="my-4">
        <h3 class="mb-3">Paiements recus</h3>
        <v-divider></v-divider>

        <v-data-table :headers="headers" :items="transactions" :items-per-page="10" class="elevation-1">
          <template v-slot:item.order_number="{ item }">
            <strong>
              #{{ item.order_number }}
            </strong>
          </template>
          <template v-slot:item.sale_amount="{ item }">
            {{ formatCurrency(item.sale_amount) }}
          </template>
          <template v-slot:item.payer="{ item }">
            {{ item.phone || "--" }}
          </template>
          <template v-slot:item.transaction_id="{ item }">
            {{ item.transaction_id || '--' }}
          </template>
          <template v-slot:item.status="{ item }">
            <v-chip
                small
                :color="getStatusChipColor(item.status)"
                :text-color="getStatusChipTextColor(item.status)"
            >
              {{ getStatusText(item.status) }}
            </v-chip>
          </template>
          <template v-slot:item.date="{ item }">
            {{ item.created_at | formatDate }}
          </template>
          <template v-slot:item.time="{ item }">
            {{ item.created_at | formatTime }}
          </template>
        </v-data-table>
      </div>

    </v-container>
    <FlashMessage :position="'right bottom'" :style="{ position: 'fixed', zIndex: 2000 }"></FlashMessage>
  </v-app>
</template>

<script>
import PreloaderComponent from '../../components/PreloaderComponent.vue'
import BreadcrumbComponent from '../../components/BreadcrumbComponent.vue'
import {Spinner} from 'vue-image-spinner2'

export default {
  name: 'WithdrawalBalanceView',
  methods: {
    openEditDialog() {
      const editableStatuses = ['REJECTED', 'DENIED', 'REVOKED', 'CANCELLED']
      if (editableStatuses.includes(this.registration.status)) {
        this.dialog = true
        return
      }

      this.accountDialog = true
    },

    formatCurrency(value) {
      const num = parseFloat(value)
      if (isNaN(num)) return '0.00'
      return num.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    load() {
      this.registration.id = null
      this.enable = false
      const self = this

      const businessId = JSON.parse(localStorage.business).id

      this.$axios.get(`/payment-processors/balance?processor_name=NATCASH&business_id=${businessId}&include_transactions=1`)
          .then(function (response) {
            self.result = response.data.result
            self.transactions = response.data.result.transactions.filter(item => item.status !== 'CANCELLED')
            self.enable = true
          }).catch(function () {
        self.enable = true
      })

      this.$axios.get(`/payment-processors?business_id=${businessId}`)
          .then(function (response) {
            self.account = response.data.find(item => item.processor_name === 'NATCASH')
            if (self.account) {
              self.registration.id = self.account.id
              self.registration.first_name = self.account.customer_first_name
              self.registration.last_name = self.account.customer_last_name
              self.registration.document_type = self.account.customer_id_type
              self.registration.document_type_number = self.account.customer_id_number
              self.registration.minimum_withdrawal_amount = self.account.minimum_withdrawal_amount
              let rawPhone = self.account.customer_phone_number || '';
              self.registration.phone = rawPhone.replace(/^(\+?509)/, '');
              self.registration.phone_verification = self.registration.phone;
              self.registration.status = self.account.status
              if (self.account.customer_id_path) {
                self.previewUrl = "https://keslakay.s3.us-east-1.amazonaws.com/" + self.account.customer_id_path
              }
            }

            self.$forceUpdate()
          })
    },

    initWithdrawal() {
      this.loading = true
      const self = this

      const businessId = JSON.parse(localStorage.business).id
      const userId = JSON.parse(localStorage.authentication).user.id

      this.$axios.post('/withdrawal-requests', {
        "processor_name": "NATCASH",
        "user_id": userId,
        "business_id": businessId,
      })
          .then(function (response) {
            self.loading = false

            if (response.data.success) {
              self.status = 'success'
              self.message = "Retrait initie avec succes"

              setTimeout(() => {
                window.location.reload()
              }, 1000)
            } else {
              self.status = 'error'
              self.message = response.data.message || "Erreur lors de l'initiation du retrait"
            }
          }).catch(function (error) {
        self.loading = false;
        self.status = 'error';
        self.message = (error.response && error.response.data && error.response.data.message) || "Erreur lors de l'initiation du retrait"
      })
    },

    getStatusChipColor(status) {
      if (status === 'APPROVED' || status === 'PAID' || status === 'CONFIRMED') return 'green lighten-4'
      if (status === 'PENDING') return 'blue lighten-4'
      return 'red lighten-4'
    },

    getStatusChipTextColor(status) {
      if (status === 'APPROVED' || status === 'PAID' || status === 'CONFIRMED') return 'green darken-3'
      if (status === 'PENDING') return 'blue darken-3'
      return 'red darken-3'
    },

    getProccesorColor(status) {
      switch (status) {
        case 'ACCEPTED':
          return 'green';
        case 'PENDING':
          return 'orange';
        case 'REJECTED':
          return 'red';
        case 'DENIED':
          return 'red';
        default:
          return 'grey';
      }
    },

    getStatusText(status) {
      if (status === 'APPROVED') return 'Approuve'
      if (status === 'PAID') return 'Paye'
      if (status === 'PENDING') return 'En attente'
      if (status === 'CONFIRMED') return 'Confirme'
      return status
    },

    register() {
      const {
        phone,
        phone_verification,
        document_type,
        document_type_number,
        first_name,
        last_name,
        file
      } = this.registration;

      if (!first_name) {
        this.flashMessage.error({
          title: 'Prenom incorrect',
          message: 'Veuillez remplir le champs Prenom.'
        });
        return;
      }

      if (!last_name) {
        this.flashMessage.error({
          title: 'Nom incorrect',
          message: 'Veuillez remplir le champs Nom.'
        });
        return;
      }

      if (!phone || !phone_verification) {
        this.flashMessage.error({
          title: 'Numero incorrect',
          message: 'Veuillez remplir les deux champs de numero Natcash.'
        });
        return;
      }

      if (phone !== phone_verification) {
        this.flashMessage.error({
          title: 'Numero incorrect',
          message: 'Les deux numeros Natcash ne correspondent pas.'
        });
        return;
      }

      if (!document_type) {
        this.flashMessage.error({
          title: 'Type de document',
          message: 'Veuillez choisir le type de document que vous fournissez.'
        });
        return;
      }

      if (!document_type_number) {
        this.flashMessage.error({
          title: 'Numero du document',
          message: 'Veuillez rentrer le numero du document.'
        });
        return;
      }

      if (document_type_number.length < 6) {
        this.flashMessage.error({
          title: 'Numero du document',
          message: 'Veuillez rentrer un numero correcte.'
        });
        return;
      }

      if (!file && this.previewUrl == null) {
        this.flashMessage.error({
          title: 'Fichier',
          message: 'Veuillez choissir un fichier document'
        });
        return;
      }

      this.loading = true;

      const businessId = JSON.parse(localStorage.business).id

      const processorData = {
        business_id: businessId,
        processor_name: "NATCASH",
        customer_first_name: first_name,
        customer_last_name: last_name,
        customer_phone_number: phone,
        customer_id_type: document_type,
        customer_id_number: document_type_number,
        status: "PENDING",
      };

      const self = this;

      const payload = `data=${encodeURIComponent(JSON.stringify(processorData))}`;
      const processorDataJson = JSON.stringify(processorData);

      if (this.registration.id == null) {
        this.$axios.post('/payment-processors', payload, {params: {data: processorDataJson,}})
            .then(function (response) {
              if (response.data.id) {
                self.flashMessage.success({
                  title: 'Succes',
                  message: 'Votre requete a ete enregistrer avec succes'
                });
                self.registration.id = response.data.id
                self.saveImageFile()
              } else {
                self.loading = false
                self.flashMessage.error({
                  title: 'Erreur',
                  message: 'Impossible d\'enregistrer votre requete'
                });
              }
            }).catch(function (error) {
          console.error('Erreur lors de la creation:', error);
          self.loading = false;
          self.flashMessage.error({
            title: 'Erreur',
            message: 'Impossible d\'enregistrer votre requete'
          });
        })
      } else {
        this.$axios.put('/payment-processors/' + this.registration.id, payload, {params: {data: processorDataJson,}})
            .then(function (response) {
              if (response.data.id) {
                self.flashMessage.success({
                  title: 'Succes',
                  message: 'Votre requete a ete enregistrer avec succes'
                });
                self.registration.id = response.data.id
                self.saveImageFile()
              } else {
                self.loading = false
                self.flashMessage.error({
                  title: 'Erreur',
                  message: 'Impossible d\'enregistrer votre requete'
                });
              }
            }).catch(function (error) {
          console.error('Erreur lors de la creation:', error);
          self.loading = false;
          self.flashMessage.error({
            title: 'Erreur',
            message: 'Impossible d\'enregistrer votre requete'
          });
        })
      }
    },

    registerMinimumAMount() {
      if (this.registration.id != null) {
        const self = this;

        if (Number(this.registration.minimum_withdrawal_amount) < 250) {
          this.flashMessage.error({
            title: 'Montant incorrecte',
            message: 'Vous ne pouvez pas definir un montant inferieure a 250 HTG'
          });
          return;
        }

        this.loading = true;

        const processorData = {
          minimum_withdrawal_amount: this.registration.minimum_withdrawal_amount,
        };

        const payload = `data=${encodeURIComponent(JSON.stringify(processorData))}`;
        const processorDataJson = JSON.stringify(processorData);

        this.$axios.put('/payment-processors/' + this.registration.id, payload, {params: {data: processorDataJson,}})
            .then(function (response) {
              if (response.data.id) {
                self.loading = false
                self.account.minimum_withdrawal_amount = self.registration.minimum_withdrawal_amount
                self.flashMessage.success({
                  title: 'Succes',
                  message: 'Montant minimum mis a jour avec succes'
                });
                self.accountDialog = false
              } else {
                self.loading = false
                self.flashMessage.error({
                  title: 'Erreur',
                  message: 'Impossible d\'enregistrer votre requete'
                });
              }
            }).catch(function (error) {
              console.error('Erreur lors de la mise a jour:', error);
              self.loading = false;
              self.flashMessage.error({
                  title: 'Erreur',
                  message: 'Impossible d\'enregistrer votre requete'
              });
        })
      }
    },

    saveImageFile() {
      if (this.registration.id && this.registration.file != null) {
        this.loading = true;
        const self = this;
        const formData = new FormData();
        formData.append('customer_id_path', this.registration.file, this.registration.file.name);

        this.$axios.post(`payment-processors/upload_image/${this.registration.id}`, formData)
            .then(function () {
              window.location.reload();
            })
            .catch(function (error) {
              self.loading = false
              console.error('Erreur upload:', error);
            });
      } else {
        window.location.reload();
      }
    },
  },
  data: () => ({
    result: null,
    transactions: [],
    account: null,
    enable: true,
    loading: false,
    dialog: false,
    accountDialog: false,
    status: null,
    message: null,
    doc_types: ['NIF', 'CIN', 'PASSPORT', 'NIU'],
    headers: [
      {text: '# Vente', value: 'order_number'},
      {text: 'MONTANT', value: 'sale_amount', align: 'center'},
      {text: 'PAYEUR', value: 'payer', align: 'center'},
      {text: 'ID. TRANSACTION', value: 'transaction_id'},
      {text: 'STATUT', value: 'status', align: 'center'},
      {text: 'DATE', value: 'date', align: 'center'},
      {text: 'HEURE', value: 'time', align: 'center'}
    ],
    items: [
      {
        text: 'ACCUEIL',
        disabled: false,
        href: '/',
      },
      {
        text: 'NATCASH',
        disabled: false,
        href: '/natcash/withdrawal-requests',
      },
      {
        text: 'BALANCE',
        disabled: true,
        href: '/natcash/balance',
      }
    ],
    registration: {
      id: null,
      first_name: '',
      last_name: '',
      email: '',
      document_type: '',
      document_type_number: 0,
      minimum_withdrawal_amount: null,
      file: null,
      phone: null,
      phone_verification: null,
      status: 'PENDING'
    },
    previewUrl: null,
    digicelPrefixes: [
      "30", "31", "34", "36", "37", "38", "39", "44", "46", "47", "48", "49"
    ],
  }),
  mounted() {
    this.load()
  },

  computed: {
    phoneRules() {
      return [
        v => !!v || 'Le numero est requis',
        v => /^\d{8}$/.test(v) || 'Le numero doit contenir exactement 8 chiffres',
        v => !this.digicelPrefixes.includes(v.substring(0, 2)) || 'Seuls les numeros Natcom sont autorises'
      ];
    },
    phoneMatchRules() {
      return [
        v => !!v || 'La verification est requise',
        v => /^\d{8}$/.test(v) || 'Le numero doit contenir exactement 8 chiffres',
        v => !this.digicelPrefixes.includes(v.substring(0, 2)) || 'Seuls les numeros Natcom sont autorises',
        v => v === this.registration.phone || 'Les numeros ne correspondent pas'
      ];
    }
  },

  watch: {
    'registration.file'(newFile) {
      if (newFile) {
        this.previewUrl = URL.createObjectURL(newFile);
      } else {
        this.previewUrl = null;
      }
    },
  },
  components: {BreadcrumbComponent, PreloaderComponent, Spinner},
}
</script>

<style scoped>
.rounded .v-input__control {
  border-radius: 12px !important;
}

.v-file-input {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  min-height: auto !important;
}

/* Balance cards */
.balance-card {
  border-radius: 12px !important;
  transition: box-shadow 0.2s ease;
}

.balance-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.balance-card--total {
  border-left: 4px solid #1565C0 !important;
  background-color: #E3F2FD !important;
}

.balance-card--due {
  border-left: 4px solid #E65100 !important;
  background-color: #FFF3E0 !important;
}

.balance-card--available {
  border-left: 4px solid #2E7D32 !important;
  background-color: #E8F5E9 !important;
}

.balance-card--fees {
  border-left: 4px solid #C62828 !important;
  background-color: #FFEBEE !important;
}

.balance-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #616161;
}

.balance-amount {
  font-size: 1.6rem;
  font-weight: 700;
  color: #212121;
  margin-top: 4px;
}

.balance-currency {
  font-size: 0.8rem;
  color: #9E9E9E;
  font-weight: 500;
  margin-top: 2px;
}

/* Account card */
.account-card {
  border-radius: 12px !important;
}

/* Account dialog info rows */
.info-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.info-label {
  font-size: 0.8rem;
  color: #9E9E9E;
  font-weight: 500;
  min-width: 160px;
}

.info-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #212121;
}
</style>
